import React, { useState } from 'react';

import { QGlobalStyles } from '@captivateiq/quotient';

import { agentIds } from '@captivateiq/assistant/config/agentConfig';

type ThinkingLoaderProps = {
  agentName?: string;
  statusMessage?: string;
};

type ThinkingVariant = 'facetCascade' | 'perimeterTrace' | 'quarterTurn';
const THINKING_VARIANTS: ThinkingVariant[] = ['facetCascade', 'perimeterTrace', 'quarterTurn'];

type MarkColors = {
  gem: string;
  spark: string;
};

// Bespoke per-agent illustration colors for the thinking mark — not a token migration gap. These
// have no Eclipse semantic equivalent (they're decorative, not brand/state colors), and they're
// wired into raw CSS custom properties via inline `style` below, which can't resolve theme token
// paths anyway. Swap colors per agent here.
const AGENT_MARK_COLORS: Record<string, MarkColors> = {
  [agentIds.COMP_BUILDER_AGENT]: { gem: '#522E8F', spark: '#DEA3FB' },
  [agentIds.COMP_OPS_AGENT]: { gem: '#195765', spark: '#C9F0C7' },
  [agentIds.REVOPS_AGENT]: { gem: '#FF804F', spark: '#F9EFBE' },
};
const DEFAULT_MARK_COLORS: MarkColors = AGENT_MARK_COLORS[agentIds.COMP_BUILDER_AGENT];

const FACET_LINES = [
  { i: 0, x1: 5, y1: 40, x2: 57.27, y2: 40 },
  { i: 1, x1: 31.68, y1: 13.32, x2: 19.67, y2: 40 },
  { i: 2, x1: 31.68, y1: 13.32, x2: 43.69, y2: 40 },
  { i: 3, x1: 31.68, y1: 66.35, x2: 43.69, y2: 39.67 },
  { i: 4, x1: 31.68, y1: 66.35, x2: 19.67, y2: 39.67 },
] as const;

const thinkingStyles = {
  '.ciq-thinking': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    fontFamily: 'Inter, Helvetica, Arial, sans-serif',
  },
  '.ciq-thinking__mark': { width: '24px', height: '24px', flex: 'none' },
  '.ciq-gem': { stroke: 'var(--ciq-gem)', strokeWidth: 5, strokeLinecap: 'round', strokeLinejoin: 'round' },
  '.ciq-thinking__label': {
    fontSize: '12px',
    lineHeight: '20px',
    fontWeight: 500,
    fontStyle: 'italic',
    // Shimmer gradient stops — decorative, no semantic token equivalent (same `background-clip:
    // text` constraint as HEADLINE_GRADIENT in AgentIntroduction.tsx).
    background: 'linear-gradient(90deg, #918E8D 0%, #918E8D 34%, #10171A 50%, #918E8D 66%, #918E8D 100%)',
    backgroundSize: '230% 100%',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
    animation: 'ciq-shimmer 2.2s linear infinite',
  },
  '@keyframes ciq-shimmer': { from: { backgroundPosition: '130% 0' }, to: { backgroundPosition: '-30% 0' } },
  '.ciq-facet': { opacity: 0.18, animation: 'ciq-facet var(--ciq-dur, 1.6s) ease-in-out infinite' },
  '.ciq-plus': {
    stroke: 'var(--ciq-plus)',
    strokeWidth: 5,
    strokeLinecap: 'round',
    animation: 'ciq-fade var(--ciq-dur, 1.6s) ease-in-out infinite',
    animationDelay: '.6s',
  },
  '.ciq-spark': {
    fill: 'var(--ciq-spark)',
    transformBox: 'fill-box',
    transformOrigin: 'center',
    animation: 'ciq-spark var(--ciq-dur, 1.6s) ease-in-out infinite',
    animationDelay: '.72s',
  },
  '@keyframes ciq-facet': { '0%, 62%, 100%': { opacity: 0.18 }, '24%': { opacity: 1 } },
  '.ciq-track': { opacity: 0.16 },
  '.ciq-trace': {
    strokeDasharray: '38 121.5',
    animation: 'ciq-trace var(--ciq-dur, 1.6s) linear infinite',
  },
  '@keyframes ciq-trace': { to: { strokeDashoffset: -159.5 } },
  '.ciq-turn-group': {
    transformBox: 'view-box',
    // The gem's true center in the 80-unit viewBox — not the icon's center. Must stay exact or
    // the diamond wobbles as it turns instead of spinning cleanly in place.
    transformOrigin: '31.68px 39.835px',
    animation: 'ciq-turn var(--ciq-dur, 1.6s) cubic-bezier(.65, 0, .35, 1) infinite',
  },
  '@keyframes ciq-turn': {
    '0%': { transform: 'rotate(0deg)' },
    '38%, 50%': { transform: 'rotate(90deg)' },
    '88%, 100%': { transform: 'rotate(180deg)' },
  },
  '@keyframes ciq-fade': { '0%, 100%': { opacity: 0.25 }, '50%': { opacity: 1 } },
  '@keyframes ciq-spark': {
    '0%, 100%': { opacity: 0.3, transform: 'scale(.7) rotate(0deg)' },
    '48%': { opacity: 1, transform: 'scale(1) rotate(45deg)' },
  },
  '@media (prefers-reduced-motion: reduce)': {
    '.ciq-thinking *': { animationDuration: '4s !important', animationTimingFunction: 'ease-in-out !important' },
    '.ciq-thinking__label': { animation: 'none', background: 'none', color: '#73706F' },
  },
} as const;

const DIAMOND_RECT_PROPS = {
  height: 39.88,
  rx: 3.68,
  ry: 3.68,
  transform: 'translate(-19.01 34.11) rotate(-45)',
  width: 39.88,
  x: 11.74,
  y: 20.06,
} as const;

const PlusAndSpark = () => (
  <>
    <g className="ciq-plus">
      <path d="M55.73,15.14h17.78" />
      <path d="M64.63,24.03V6.25" />
    </g>
    <path
      className="ciq-spark"
      d="M64.09,53.38c.18-.5.89-.5,1.08,0l2.45,6.67c.06.16.18.28.34.34l6.67,2.45c.5.18.5.89,0,1.08l-6.67,2.45c-.16.06-.28.18-.34.34l-2.45,6.67c-.18.5-.89.5-1.08,0l-2.45-6.67c-.06-.16-.18-.28-.34-.34l-6.67-2.45c-.5-.18-.5-.89,0-1.08l6.67-2.45c.16-.06.28-.18.34-.34l2.45-6.67Z"
    />
  </>
);

// "Facet Cascade" — the diamond's internal facet lines pulse in sequence.
const FacetCascadeMark = () => (
  <>
    <g className="ciq-gem">
      <rect {...DIAMOND_RECT_PROPS} />
      {FACET_LINES.map((line) => (
        <line
          className="ciq-facet"
          key={line.i}
          style={{ animationDelay: `${line.i * 0.12}s` }}
          x1={line.x1}
          x2={line.x2}
          y1={line.y1}
          y2={line.y2}
        />
      ))}
    </g>
    <PlusAndSpark />
  </>
);

// "Perimeter Trace" — a dashed outline continuously traces the diamond's edge, with the internal
// facets shown as static lines at the same dim color/opacity as the outline's resting ("track")
// state — they never pulse on their own the way Facet Cascade's do.
const PerimeterTraceMark = () => (
  <>
    <g className="ciq-gem">
      <rect className="ciq-track" {...DIAMOND_RECT_PROPS} />
      <rect className="ciq-trace" {...DIAMOND_RECT_PROPS} />
      {FACET_LINES.map((line) => (
        <line className="ciq-track" key={line.i} x1={line.x1} x2={line.x2} y1={line.y1} y2={line.y2} />
      ))}
    </g>
    <PlusAndSpark />
  </>
);

// "Quarter Turn" — the whole diamond rotates in place; the facet pattern is symmetric about the
// gem's center, so each 90° landing still reads as the complete mark and the loop never shows a
// broken frame.
const QuarterTurnMark = () => (
  <>
    <g className="ciq-gem ciq-turn-group">
      <rect {...DIAMOND_RECT_PROPS} />
      {FACET_LINES.map((line) => (
        <line key={line.i} x1={line.x1} x2={line.x2} y1={line.y1} y2={line.y2} />
      ))}
    </g>
    <PlusAndSpark />
  </>
);

type ThinkingMarkProps = {
  agentName?: string;
  /** Defaults to 24px (the size used inline with the "Thinking…" label). */
  size?: number;
};

// Just the animated gem — no "Thinking…" label. Picked once per mount so it stays stable for
// the life of one loading state, and a different variant may come up next time. Shared by
// ThinkingLoader below and anywhere else that wants this same animation without the label
// (e.g. an "Analyzing…" state elsewhere in the app).
export const ThinkingMark = ({ agentName, size = 24 }: ThinkingMarkProps) => {
  const colors = (agentName && AGENT_MARK_COLORS[agentName]) || DEFAULT_MARK_COLORS;
  const [variant] = useState<ThinkingVariant>(
    () => THINKING_VARIANTS[Math.floor(Math.random() * THINKING_VARIANTS.length)],
  );

  return (
    <>
      <QGlobalStyles styles={thinkingStyles} />
      <svg
        aria-hidden="true"
        className="ciq-thinking__mark"
        fill="none"
        focusable="false"
        style={
          {
            width: `${size}px`,
            height: `${size}px`,
            '--ciq-gem': colors.gem,
            '--ciq-plus': colors.gem,
            '--ciq-spark': colors.spark,
            '--ciq-dur': '1.6s',
          } as React.CSSProperties
        }
        viewBox="0 0 80 80"
      >
        {variant === 'facetCascade' && <FacetCascadeMark />}
        {variant === 'perimeterTrace' && <PerimeterTraceMark />}
        {variant === 'quarterTurn' && <QuarterTurnMark />}
      </svg>
    </>
  );
};

export const ThinkingLoader = ({ agentName, statusMessage }: ThinkingLoaderProps) => (
  <span aria-live="polite" className="ciq-thinking" data-testid="ai-thinking-loader" role="status">
    <ThinkingMark agentName={agentName} />
    <span className="ciq-thinking__label">{statusMessage || 'Thinking…'}</span>
  </span>
);
